import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type React from 'react';
import { useCallback, useState } from 'react';

import * as styles from './calendar-scheduler.css';
import type { CalendarEvent, EventRenderer, EventResizeHandler } from './types';

const formatTimeDisplay = (time24: string): string => {
  const parts = time24.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const GripVerticalIcon = () => (
  <svg
    width="16"
    height="23"
    viewBox="0 0 16 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.33334 5.75H5.33758M5.33334 11.5H5.33758M5.33334 17.25H5.33758M10.6625 5.75H10.6667M10.6625 11.5H10.6667M10.6625 17.25H10.6667"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


interface EventItemProps {
  event: CalendarEvent;
  onEdit: (event: CalendarEvent) => void;
  onResize: EventResizeHandler;
  isDragOverlay?: boolean;
  renderEvent?: EventRenderer;
  column?: number;
  totalColumns?: number;
}

export const EventItem: React.FC<EventItemProps> = ({
  event,
  onEdit,
  onResize,
  isDragOverlay = false,
  renderEvent,
  column = 0,
  totalColumns = 1,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const isPreview = event.isPreview === true;
  const isReadonly = event.readonly === true || isPreview;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: event.id,
      data: {
        eventId: event.id,
        event,
      },
      disabled: isResizing || isReadonly,
    });

  const getEventPosition = () => {
    const startParts = event.startTime.split(':').map(Number);
    const endParts = event.endTime.split(':').map(Number);
    const startHour = startParts[0] ?? 0;
    const startMinute = startParts[1] ?? 0;
    const endHour = endParts[0] ?? 0;
    const endMinute = endParts[1] ?? 0;

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    const totalMinutes = 24 * 60;
    const top = (startMinutes / totalMinutes) * 100;
    const height = ((endMinutes - startMinutes) / totalMinutes) * 100;

    return { top: `${top}%`, height: `calc(${height}% - 2px)` };
  };

  const { top, height } = getEventPosition();

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (isReadonly) return;
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      const startY = e.clientY;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const container = document.querySelector(`.${styles.timeGridContent}`);
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const totalHeight = containerRect.height;
        const deltaY = moveEvent.clientY - startY;
        const deltaMinutes =
          Math.round(((deltaY / totalHeight) * (24 * 60)) / 15) * 15;

        const endParts = event.endTime.split(':').map(Number);
        const endHour = endParts[0] ?? 0;
        const endMinute = endParts[1] ?? 0;
        let newEndMinutes = endHour * 60 + endMinute + deltaMinutes;

        const startParts = event.startTime.split(':').map(Number);
        const startHour = startParts[0] ?? 0;
        const startMinute = startParts[1] ?? 0;
        const startMinutes = startHour * 60 + startMinute;

        if (newEndMinutes < startMinutes + 15) {
          newEndMinutes = startMinutes + 15;
        }

        if (newEndMinutes > 24 * 60) {
          newEndMinutes = 24 * 60;
        }

        const newEndHour = Math.floor(newEndMinutes / 60);
        const newEndMin = newEndMinutes % 60;

        const formattedEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;

        onResize(event, formattedEndTime);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [event, onResize, isReadonly]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-resize-handle]')) {
        return;
      }
      if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
        return;
      }
      if (isDragging || isResizing) {
        return;
      }
      onEdit(event);
    },
    [event, onEdit, isDragging, isResizing]
  );

  const isDraggingState = isDragging || isDragOverlay;
  const customContent = renderEvent?.({
    event,
    isDragging: isDraggingState,
    isResizing,
  });

  const dragStyle = isDragOverlay
    ? {}
    : {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? (customContent ? 0 : 0.3) : 1,
        pointerEvents: isDragging && customContent ? ('none' as const) : undefined,
      };

  const widthPercent = totalColumns > 1 ? 100 / totalColumns : 100;
  const leftPercent = column * widthPercent;
  const gap = totalColumns > 1 ? 2 : 0;

  const positionStyle = isDragOverlay
    ? {}
    : {
        top,
        height,
        minHeight: '16px',
        width:
          totalColumns > 1 ? `calc(${widthPercent}% - ${gap}px)` : undefined,
        left:
          totalColumns > 1 ? `calc(${leftPercent}% + ${gap / 2}px)` : undefined,
        right: totalColumns > 1 ? 'auto' : undefined,
      };

  const canDrag = !isDragOverlay && !isReadonly;
  const canResize = !isDragOverlay && !isReadonly;

  return (
    <div
      ref={!isDragOverlay ? setNodeRef : undefined}
      className={customContent ? styles.eventItemCustom : styles.eventItem}
      style={{ ...positionStyle, ...dragStyle }}
      data-event-id={event.id}
      data-category={event.category}
      data-resizing={isResizing}
      data-dragging={isDragging}
      data-drag-overlay={isDragOverlay}
      data-preview={isPreview || undefined}
      data-readonly={isReadonly || undefined}
      onClick={handleClick}
      {...(canDrag && !customContent ? attributes : {})}
      {...(canDrag && !customContent ? listeners : {})}
    >
      {customContent ? (
        <div
          className={styles.eventCustomContent}
          {...(canDrag ? attributes : {})}
          {...(canDrag ? listeners : {})}
          style={{ touchAction: isReadonly ? 'auto' : 'none' }}
        >
          {customContent}
        </div>
      ) : (
        <div className={styles.eventContent}>
          {!isReadonly && (
            <div
              className={styles.eventDragHandle}
              data-drag-handle="true"
              {...(canDrag ? attributes : {})}
              {...(canDrag ? listeners : {})}
            >
              <GripVerticalIcon />
            </div>
          )}
          <div className={styles.eventDetails}>
            <div className={styles.eventTime}>{formatTimeDisplay(event.startTime)}</div>
            <div className={styles.eventTitle}>{event.title}</div>
          </div>
        </div>
      )}

      {canResize && (
        <div
          className={styles.eventResizeHandle}
          data-resize-handle="true"
          data-resizing={isResizing || undefined}
          onMouseDown={handleResizeStart}
        />
      )}
    </div>
  );
};
